/* Solia Wall — Cookie consent banner
 *
 * Compliance:
 * - California CCPA/CPRA: Global Privacy Control (GPC) signal honored as opt-out of "sale/share"
 * - Virginia VCDPA + other state privacy laws: equivalent treatment
 * - EU GDPR / UK GDPR / ePrivacy Directive: opt-IN consent required before non-essential cookies fire
 * - Strictly necessary cookies always run (cart, session, CSRF, fraud prevention)
 * - User can revisit choices any time via the "Manage cookies" footer link
 *
 * Storage:
 * - Decision stored in localStorage key "hw_consent" as {essential:true, analytics:bool, marketing:bool, ts:int}
 * - "hw_consent_v" = 1 lets us bump if we add new categories later
 */

(function () {
  const STORAGE_KEY = 'hw_consent';
  const STORAGE_V = 'hw_consent_v';
  const CURRENT_VERSION = 1;

  // Detect GPC signal
  const gpc = (navigator.globalPrivacyControl === true || navigator.doNotTrack === '1');

  function getStored() {
    try {
      const v = parseInt(localStorage.getItem(STORAGE_V) || '0', 10);
      if (v !== CURRENT_VERSION) return null;
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function setStored(prefs) {
    try {
      localStorage.setItem(STORAGE_V, String(CURRENT_VERSION));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.assign({}, prefs, { ts: Date.now() })));
    } catch (e) {
      // localStorage blocked — fall through silently
    }
  }

  function applyConsent(prefs) {
    // Hook for downstream scripts. Each integration reads window.HWConsent to decide whether to fire.
    window.HWConsent = {
      essential: true, // always
      analytics: !!prefs.analytics && !gpc,
      marketing: !!prefs.marketing && !gpc,
      gpc: gpc,
      timestamp: prefs.ts || Date.now()
    };
    // Dispatch event for late-bound scripts
    try {
      window.dispatchEvent(new CustomEvent('hw:consent', { detail: window.HWConsent }));
    } catch (e) {}
  }

  function buildBanner() {
    const wrap = document.createElement('div');
    wrap.id = 'hw-cookie-banner';
    wrap.setAttribute('role', 'dialog');
    wrap.setAttribute('aria-modal', 'false');
    wrap.setAttribute('aria-label', 'Cookie preferences');
    wrap.innerHTML = `
<div class="hw-cookie-banner__inner">
  <div class="hw-cookie-banner__copy">
    <strong>Cookies on Solia Wall.</strong>
    We use cookies that are essential for the site to function (cart, sign-in, fraud prevention). With your permission, we'd also like to use analytics cookies to understand how the site is used, and marketing cookies for Meta and Google ad measurement. ${gpc ? '<em>We detected your browser is sending a Global Privacy Control signal — analytics and marketing cookies are off by default.</em>' : ''}
    <a href="./policies/cookies.html" class="hw-cookie-banner__learn" data-cookies-link>Read the full Cookie Policy</a>.
  </div>
  <div class="hw-cookie-banner__buttons">
    <button class="hw-cookie-banner__btn hw-cookie-banner__btn--ghost" data-action="reject">Reject non-essential</button>
    <button class="hw-cookie-banner__btn hw-cookie-banner__btn--ghost" data-action="customize">Customize</button>
    <button class="hw-cookie-banner__btn hw-cookie-banner__btn--primary" data-action="accept">Accept all</button>
  </div>
</div>`;
    // Adjust the policy link prefix based on page location
    const pathDepth = (location.pathname.split('/').filter(p => p && p.endsWith('.html') === false).length);
    if (pathDepth > 0) {
      const link = wrap.querySelector('[data-cookies-link]');
      if (link) link.href = '../policies/cookies.html';
    }
    return wrap;
  }

  function buildPreferences() {
    const overlay = document.createElement('div');
    overlay.id = 'hw-cookie-prefs';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Cookie preference center');
    const stored = getStored() || {};
    overlay.innerHTML = `
<div class="hw-cookie-prefs__modal">
  <button class="hw-cookie-prefs__close" data-action="close" aria-label="Close">×</button>
  <h3 class="hw-cookie-prefs__title">Cookie preferences</h3>
  <p class="hw-cookie-prefs__lead">Choose which categories of cookies you allow on Solia Wall. Your choice is stored on this device and you can change it any time via the "Manage cookies" link in the footer.</p>

  <div class="hw-cookie-prefs__row">
    <div>
      <strong>Strictly necessary</strong>
      <p>Required for the site to function — cart, sign-in, CSRF protection, fraud prevention. Cannot be disabled.</p>
    </div>
    <div class="hw-cookie-prefs__switch hw-cookie-prefs__switch--locked">Always on</div>
  </div>

  <div class="hw-cookie-prefs__row">
    <div>
      <strong>Analytics</strong>
      <p>Aggregated, anonymized data on how visitors use the site (e.g. which pages are visited). Helps us improve the product. No personal data is sold.</p>
    </div>
    <label class="hw-cookie-prefs__switch">
      <input type="checkbox" data-pref="analytics" ${stored.analytics ? 'checked' : ''}>
      <span class="hw-cookie-prefs__slider"></span>
    </label>
  </div>

  <div class="hw-cookie-prefs__row">
    <div>
      <strong>Marketing</strong>
      <p>Used to measure ad performance on Meta and Google so we can stop spending on ads that don't work. Toggling off opts you out of personalized ads from us, and (where applicable) is treated as an opt-out of "sale/share" of personal information under CCPA/CPRA.</p>
    </div>
    <label class="hw-cookie-prefs__switch">
      <input type="checkbox" data-pref="marketing" ${stored.marketing ? 'checked' : ''}>
      <span class="hw-cookie-prefs__slider"></span>
    </label>
  </div>

  ${gpc ? '<p class="hw-cookie-prefs__gpc">Your browser is sending a Global Privacy Control signal. We honor that signal and will not set analytics or marketing cookies regardless of the toggles above.</p>' : ''}

  <div class="hw-cookie-prefs__actions">
    <button class="hw-cookie-banner__btn hw-cookie-banner__btn--ghost" data-action="reject-all">Reject non-essential</button>
    <button class="hw-cookie-banner__btn hw-cookie-banner__btn--primary" data-action="save">Save preferences</button>
  </div>
</div>`;
    return overlay;
  }

  function injectStyles() {
    if (document.getElementById('hw-cookie-styles')) return;
    const s = document.createElement('style');
    s.id = 'hw-cookie-styles';
    s.textContent = `
#hw-cookie-banner{position:fixed;left:16px;right:16px;bottom:16px;z-index:9999;background:#fff;border:1px solid #cdd6e0;border-top:3px solid #C9A961;border-radius:10px;box-shadow:0 12px 32px rgba(45,70,89,.18);font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#1f2a36;animation:hwSlideUp .3s ease}
@keyframes hwSlideUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
.hw-cookie-banner__inner{display:flex;align-items:center;gap:20px;padding:16px 20px;max-width:1280px;margin:0 auto;flex-wrap:wrap}
.hw-cookie-banner__copy{flex:1;min-width:260px;font-size:13px;line-height:1.55}
.hw-cookie-banner__copy strong{display:block;margin-bottom:4px;font-size:14px}
.hw-cookie-banner__copy em{display:block;margin-top:4px;color:#2D4659;font-style:italic}
.hw-cookie-banner__learn{color:#2D4659;text-decoration:underline;margin-left:4px}
.hw-cookie-banner__buttons{display:flex;gap:8px;flex-wrap:wrap}
.hw-cookie-banner__btn{font-family:inherit;font-size:13px;padding:9px 14px;border-radius:6px;cursor:pointer;border:1px solid #cdd6e0;background:#fff;color:#1f2a36;font-weight:500;transition:all .15s}
.hw-cookie-banner__btn:hover{border-color:#2D4659}
.hw-cookie-banner__btn--primary{background:#2D4659;color:#fff;border-color:#2D4659}
.hw-cookie-banner__btn--primary:hover{background:#1f3247}
.hw-cookie-banner__btn--ghost{background:transparent}

#hw-cookie-prefs{position:fixed;inset:0;background:rgba(15,20,28,.55);z-index:10000;display:flex;align-items:center;justify-content:center;padding:20px;animation:hwFadeIn .2s ease}
@keyframes hwFadeIn{from{opacity:0}to{opacity:1}}
.hw-cookie-prefs__modal{background:#FAF6EE;max-width:560px;width:100%;max-height:90vh;overflow-y:auto;border-radius:12px;padding:32px;position:relative;border-top:4px solid #C9A961;box-shadow:0 20px 60px rgba(0,0,0,.3);font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#1f2a36}
.hw-cookie-prefs__close{position:absolute;top:12px;right:14px;background:none;border:none;font-size:28px;line-height:1;cursor:pointer;color:#2D4659}
.hw-cookie-prefs__title{margin:0 0 8px;font-family:Georgia,serif;font-style:italic;font-size:24px;color:#2D4659}
.hw-cookie-prefs__lead{font-size:13px;line-height:1.55;color:#3f4a55;margin-bottom:18px}
.hw-cookie-prefs__row{display:flex;justify-content:space-between;align-items:flex-start;gap:16px;padding:14px 0;border-top:1px solid #e1e6ec}
.hw-cookie-prefs__row strong{display:block;font-size:14px;margin-bottom:4px}
.hw-cookie-prefs__row p{font-size:12px;line-height:1.5;color:#5a6470;margin:0}
.hw-cookie-prefs__switch{position:relative;display:inline-block;width:44px;height:24px;flex-shrink:0;cursor:pointer}
.hw-cookie-prefs__switch--locked{width:auto;height:auto;font-size:11px;color:#8a929c;text-transform:uppercase;letter-spacing:.05em;cursor:default}
.hw-cookie-prefs__switch input{opacity:0;width:0;height:0}
.hw-cookie-prefs__slider{position:absolute;cursor:pointer;inset:0;background:#cdd6e0;border-radius:24px;transition:.2s}
.hw-cookie-prefs__slider:before{position:absolute;content:"";height:18px;width:18px;left:3px;top:3px;background:#fff;border-radius:50%;transition:.2s}
.hw-cookie-prefs__switch input:checked+.hw-cookie-prefs__slider{background:#C9A961}
.hw-cookie-prefs__switch input:checked+.hw-cookie-prefs__slider:before{transform:translateX(20px)}
.hw-cookie-prefs__gpc{margin-top:18px;padding:12px;background:#fff;border:1px dashed #C9A961;border-radius:8px;font-size:12px;line-height:1.5;color:#3f4a55}
.hw-cookie-prefs__actions{margin-top:22px;display:flex;gap:10px;justify-content:flex-end;flex-wrap:wrap}
@media(max-width:520px){.hw-cookie-banner__inner{padding:14px}.hw-cookie-banner__buttons{width:100%}.hw-cookie-banner__btn{flex:1;min-width:0}}
`;
    document.head.appendChild(s);
  }

  function showBanner() {
    if (document.getElementById('hw-cookie-banner')) return;
    const b = buildBanner();
    document.body.appendChild(b);
    b.addEventListener('click', (e) => {
      const action = e.target.dataset && e.target.dataset.action;
      if (!action) return;
      if (action === 'accept') {
        const prefs = { essential: true, analytics: !gpc, marketing: !gpc };
        setStored(prefs);
        applyConsent(prefs);
        b.remove();
      } else if (action === 'reject') {
        const prefs = { essential: true, analytics: false, marketing: false };
        setStored(prefs);
        applyConsent(prefs);
        b.remove();
      } else if (action === 'customize') {
        b.remove();
        openPreferences();
      }
    });
  }

  function openPreferences() {
    closePreferences();
    injectStyles();
    const overlay = buildPreferences();
    document.body.appendChild(overlay);
    overlay.addEventListener('click', (e) => {
      const action = e.target.dataset && e.target.dataset.action;
      if (e.target === overlay || action === 'close') {
        closePreferences();
        return;
      }
      if (action === 'reject-all') {
        const prefs = { essential: true, analytics: false, marketing: false };
        setStored(prefs);
        applyConsent(prefs);
        closePreferences();
        const banner = document.getElementById('hw-cookie-banner');
        if (banner) banner.remove();
      } else if (action === 'save') {
        const a = overlay.querySelector('[data-pref="analytics"]');
        const m = overlay.querySelector('[data-pref="marketing"]');
        const prefs = { essential: true, analytics: !!(a && a.checked) && !gpc, marketing: !!(m && m.checked) && !gpc };
        setStored(prefs);
        applyConsent(prefs);
        closePreferences();
        const banner = document.getElementById('hw-cookie-banner');
        if (banner) banner.remove();
      }
    });
  }

  function closePreferences() {
    const ex = document.getElementById('hw-cookie-prefs');
    if (ex) ex.remove();
  }

  // Public API
  window.HWCookies = { openPreferences: openPreferences, reset: function () { try { localStorage.removeItem(STORAGE_KEY); localStorage.removeItem(STORAGE_V); } catch (e) {} location.reload(); } };

  // Boot
  function boot() {
    // Consent banner REMOVED site-wide (2026-09-13) to cut funnel friction.
    // The banner never renders. Consent is treated as granted by default so any
    // downstream reader of window.HWConsent behaves exactly as if the visitor had
    // accepted (applyConsent still forces analytics/marketing off when the browser
    // sends a GPC signal, so GPC users are still honored). The "Manage cookies"
    // footer link still works via window.HWCookies.openPreferences().
    const stored = getStored();
    applyConsent(stored || { essential: true, analytics: true, marketing: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
